import { useNavigate, useSearchParams } from '@hulu/react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { PageWrapper } from '../../components/BlueTheme';
import Footer from '../../components/Footer/Footer';
import EnterCodeStep from '../../components/MFA/EnterCodeStep/EnterCodeStep';
import MultiFactorAuthEnabled from '../../components/MFA/MultiFactorAuthEnabled/MultiFactorAuthEnabled';
import SetPhoneStep from '../../components/MFA/SetPhoneStep/SetPhoneStep';
import StepFooter from '../../components/MFA/StepFooter/StepFooter';
import { StyledLoader, StyledVerticalFlexContainer } from '../../components/styled';
import { RoutePath } from '../../constants';
import { useAWSCognitoUser, useMFA } from '../../hooks';
import { MFAStep } from '../../hooks/useMFA/constants';
import { useUpdateDocumentTitle } from '../../hooks/useUpdateDocumentTitle';
import { getRedirectSearchPath } from '../../utils/redirect';
import SetPasswordStep from '../SetPassword/SetPasswordStep/SetPasswordStep';
import EmailPasswordStep from './EmailPasswordStep/EmailPasswordStep';
import { FormWrapper } from './styled';
import { LoginSearchParams } from './types';

const Login = (): JSX.Element => {
  useUpdateDocumentTitle('Disney Campaign Manager');

  const [searchParams] = useSearchParams();
  const userSearchParams: LoginSearchParams = useMemo(
    () => ({
      email: searchParams.get('email') || '',
      token: searchParams.get('token') || '',
      firstName: searchParams.get('userFirstName'),
      lastName: searchParams.get('userLastName'),
    }),
    [searchParams]
  );
  const isCheckCognitoUserNeeded = !!(userSearchParams.email?.length && userSearchParams.token?.length);
  const [checkCognitoUserLoading, setCheckCognitoUserLoading] = useState(isCheckCognitoUserNeeded);
  const [saveDevice, setSaveDevice] = useState(false);
  const { checkSetPasswordIsNeeded, logout } = useAWSCognitoUser();
  const navigate = useNavigate();

  const onAfterMFA = useCallback(() => {
    navigate({
      pathname: RoutePath.ORG_SWITCHER,
      search: getRedirectSearchPath(searchParams),
    });
  }, [navigate, searchParams]);

  const {
    mfaStep,
    setPhone,
    loading,
    verifyCode,
    resendCode,
    onLogin,
    errorToShow,
    setErrorToShow,
    setupMfaFlow,
    passwordStep,
    onResetPassword,
  } = useMFA({
    onAfterMFA,
    saveDevice,
  });

  const checkCognitoUserIsNeeded = useCallback(async (): Promise<void> => {
    if (isCheckCognitoUserNeeded) {
      try {
        const setPasswordIsNeeded = await checkSetPasswordIsNeeded(userSearchParams.email, userSearchParams.token);

        if (setPasswordIsNeeded) {
          await logout(false);
          navigate(RoutePath.SET_PASSWORD, {
            state: userSearchParams,
          });
        } else {
          navigate(RoutePath.LOGIN);
        }
      } catch {
        setCheckCognitoUserLoading(false);
      }
    } else {
      setCheckCognitoUserLoading(false);
    }
  }, [checkSetPasswordIsNeeded, isCheckCognitoUserNeeded, logout, navigate, userSearchParams]);

  useEffect(() => {
    void checkCognitoUserIsNeeded();

    return () => setCheckCognitoUserLoading(false);
  }, [checkCognitoUserIsNeeded]);

  if (checkCognitoUserLoading) {
    return <StyledLoader data-testid="loginLoader" />;
  }

  return (
    <StyledVerticalFlexContainer data-testid="loginContent">
      <PageWrapper>
        <FormWrapper>
          {passwordStep ? (
            <SetPasswordStep loading={loading} onSetPassword={onResetPassword} passwordStep={passwordStep} />
          ) : (
            !mfaStep && <EmailPasswordStep onLogin={onLogin} loading={loading} />
          )}
          {mfaStep === MFAStep.SET_PHONE && <SetPhoneStep onSetPhone={setPhone} loading={loading} />}
          {mfaStep === MFAStep.ENTER_SMS_CODE && (
            <EnterCodeStep
              setErrorToShow={setErrorToShow}
              errorToShow={errorToShow}
              loading={loading}
              verifyCode={verifyCode}
              resendCode={resendCode}
              setupMfaFlow={setupMfaFlow}
              saveDevice={saveDevice}
              setSaveDevice={(): void => setSaveDevice(!saveDevice)}
            />
          )}
          {mfaStep === MFAStep.MULTI_FACTOR_AUTH_ENABLED && <MultiFactorAuthEnabled />}
          <StepFooter />
        </FormWrapper>
      </PageWrapper>
      <Footer isBaymax />
    </StyledVerticalFlexContainer>
  );
};

export default Login;
